import React, { useRef, useContext } from "react"
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog"
import { Toast } from "primereact/toast"
import { UserContext } from "../../../context/userContext"
import { useMutation } from "@apollo/client"
import { CHANGE_STATUS_ORGANIZATION, GET_ORGANIZATIONS } from "../queries"
import { InputSwitch } from "primereact/inputswitch"
import { Tooltip } from "primereact/tooltip"
import { LogError } from "../../../helpers/logger"

export default function StatusChangeDialog(props) {
  const toast = useRef(null)
  const userCtx = useContext(UserContext)
  const [changeStatusOrganization] = useMutation(CHANGE_STATUS_ORGANIZATION)
  const isEnterpriseOrganization =
    props.data.organization_id === userCtx.session.user.enterprise_id

  const handleOnChange = (value, organization_id) => {
    changeStatusOrganization({
      variables: {
        staff_id: userCtx?.session.user.staff_id,
        organization_id: organization_id,
        organization_status: value,
      },
      skip: !userCtx.session.user.staff_id,
      refetchQueries: [
        {
          query: GET_ORGANIZATIONS,
          variables: {
            staff_id: userCtx?.session.user.staff_id,
            organization_id: userCtx?.session.user.organization_id,
          },
        },
      ],
      fetchPolicy: "no-cache",
    }).then(async () => {
      //Check if the current enterprise is being deactivated and change the selection if it is.
      if (
        userCtx.session.user.organization_id === organization_id &&
        value === "INACTIVE"
      ) {
        try {
          const enterpriseRef = userCtx.session.enterprise_list.find(
            (enterpriseRef) =>
              enterpriseRef.id === userCtx.session.user.enterprise_id
          )
          if (enterpriseRef) {
            const organizationRefs = await enterpriseRef
              .collection("organizations")
              .get()

            for (const doc of organizationRefs.docs) {
              if (
                doc.id !== userCtx.session.user.organization_id &&
                doc.data().organization_status === "ACTIVE"
              ) {
                await userCtx.changeOrganization(doc.id)
                return // stop looping once a valid organization is found
              }
            }

            //No active organizations found, switch to enterprise organization
            if (userCtx.session.user.enterprise_id) {
              await userCtx.changeOrganization(
                userCtx.session.user.enterprise_id
              )
              return
            }
            LogError(
              "Fallback to enterprise organization failed: enterprise_id is missing from user session."
            )
          }
        } catch (error) {
          LogError("Unable to switch organizations", error)
        }
      }

      // Refresh data if not switching, or if no other active enterprise was found.
      userCtx.refreshData()
    })
  }

  const accept = () => {
    const newStatus =
      props.data.organization_status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
    handleOnChange(newStatus, props.data.organization_id)

    let message = ""
    let title = ""
    switch (newStatus) {
      case "ACTIVE":
        title = "Organization activated"
        message = props.data.organization_name + " has been activated"
        break
      case "INACTIVE":
        title = "Organization inactivated"
        message = props.data.organization_name + " has been inactivated"
        break

      default:
        break
    }

    toast.current.show({
      severity: "info",
      summary: title,
      detail: message,
      life: 3000,
    })
  }

  const reject = () => {
    toast.current.show({
      severity: "warn",
      summary: "Rejected",
      detail: "You have rejected",
      life: 3000,
    })
  }

  const confirmChange = () => {
    if (props.data.organization_status === "ACTIVE") {
      confirmDialog({
        message:
          "Making the Organization Inactive will close ALL staff user sessions and close access to RecoveryLink for the organization.",
        header: "Confirmation of Inactivation",
        icon: "pi pi-info-circle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        accept,
        reject,
      })
    } else {
      confirmDialog({
        message:
          "Do you want to activate this Organization? All staff members will need to be reviewed and activated individually.",
        header: "Confirmation",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "accept",
        accept,
        reject,
      })
    }
  }

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <Tooltip target={".orgStatus-tt"} className="custom-tooltip" />
      <div
        className="orgStatus-tt"
        data-pr-tooltip={
          isEnterpriseOrganization
            ? "Enterprise Organizations cannot be disabled"
            : ""
        }
        data-pr-position="left"
      >
        <InputSwitch
          checked={props.data.organization_status === "ACTIVE"}
          onChange={confirmChange}
          disabled={isEnterpriseOrganization}
        />
      </div>
    </>
  )
}
